import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { FEATURE_GROUP_LABELS, GROUP_NOTES, type Control, type FeatureGroup } from '@guest/shared';
import { useHouse } from '../lib/store.js';
import { AppBar } from '../components/Chrome.js';
import { Panel } from '../components/ui.js';
import { CameraTile, ControlRow, SensorGrid } from '../components/controls.js';

const GROUP_ICONS: Record<FeatureGroup, string> = {
  lighting: 'bulb',
  climate: 'radiator',
  covers: 'blind',
  media: 'music',
  sensors: 'motion',
  cameras: 'camera',
  other: 'power',
};

const GROUP_ORDER: FeatureGroup[] = [
  'lighting',
  'climate',
  'covers',
  'media',
  'other',
  'cameras',
  'sensors',
];

function GroupPanel({ group, controls }: { group: FeatureGroup; controls: Control[] }): ReactNode {
  const { states } = useHouse();
  if (controls.length === 0) return null;

  const body =
    group === 'sensors' ? (
      <SensorGrid controls={controls} states={states} />
    ) : group === 'cameras' ? (
      <div className="camera-grid">
        {controls.map((c) => (
          <CameraTile key={c.entityId} control={c} />
        ))}
      </div>
    ) : (
      controls.map((c) => <ControlRow key={c.entityId} control={c} state={states.get(c.entityId)} />)
    );

  return (
    <Panel
      title={FEATURE_GROUP_LABELS[group]}
      icon={GROUP_ICONS[group]}
      note={GROUP_NOTES[group]}
      className="stack"
      key={group}
    >
      {body}
    </Panel>
  );
}

export function RoomScreen(): ReactNode {
  const { roomId } = useParams<{ roomId: string }>();
  const { context } = useHouse();

  if (!context) return <div className="empty">Loading…</div>;
  const room = context.rooms.find((r) => r.id === roomId);
  if (!room) return <Navigate to="/rooms" replace />;

  // Rooms with zones (a bedroom plus its ensuite) are split, so a guest sees
  // "Bedroom / Ensuite" rather than one undifferentiated list of 17 controls.
  const zones = [...new Set(room.controls.map((c) => c.zone ?? ''))];
  const hasZones = zones.length > 1 || (zones.length === 1 && zones[0] !== '');

  const renderZone = (zone: string): ReactNode => {
    const inZone = room.controls.filter((c) => (c.zone ?? '') === zone);
    return (
      <div key={zone || 'all'}>
        {hasZones && zone && <div className="zone-label">{zone}</div>}
        <div className="stack" style={{ gap: 'var(--space-3)' }}>
          {GROUP_ORDER.map((g) => (
            <GroupPanel key={g} group={g} controls={inZone.filter((c) => c.group === g)} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <AppBar title={room.name} subtitle={room.blurb} back />
      <main className="shell">
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          {zones.map(renderZone)}
        </div>
      </main>
    </>
  );
}
